import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0E14]">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="bg-white rounded-md px-1.5 py-1 flex items-center border"><img src="/mesabi-logo.png" alt="Mesabi Metallics" className="h-7 w-auto" /></div>
          <span className="font-display font-bold text-lg text-slate-100">Mesabi Metallics</span>
        </div>
        {done ? (
          <div data-testid="reset-success" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
            <h2 className="font-display text-xl font-bold text-slate-100 mt-3">Password updated</h2>
            <p className="text-sm text-slate-400 mt-1">Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <div data-testid="reset-invalid" className="rounded-xl border border-essred/30 bg-essred/10 p-6">
            <h2 className="font-display text-xl font-bold text-slate-100">Invalid reset link</h2>
            <p className="text-sm text-slate-400 mt-1">This link is missing its token. Please request a new reset email.</p>
            <Button data-testid="reset-back-login" onClick={() => nav("/login")} className="mt-4 w-full bg-[#F5A623] hover:bg-[#e0961a] text-[#0A0E14]">Back to sign in</Button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-2xl font-bold text-slate-100">Choose a new password</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Enter a new password for your workspace account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" data-testid="reset-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input id="confirm-password" data-testid="reset-confirm-input" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" required />
              </div>
              {error && <div data-testid="reset-error" className="text-sm text-essred bg-essred/5 border border-essred/20 rounded-lg px-3 py-2">{error}</div>}
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#F5A623] hover:bg-[#e0961a] text-[#0A0E14] h-11">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
